import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { parseBarcode, type StockCountDto, type StockCountPosition, vnDate } from "@hisense/shared";
import { useReceiptWarehouses } from "@/api/warehouseReceipts";
import { useStockCountPositions } from "@/api/stockCounts";
import { apiFetch } from "@/lib/api";
import { formatQty } from "@/domain/money";
import { Button } from "@/components/ui/Button";
import { FieldLabel, Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { useStockMoveAccess } from "@/components/warehouse/StockMoveRules";
import { ScanField, cleanQuantity, lotScan, sameProduct, scanError } from "./ScanField";

const today = () => vnDate();
type Counted = { position: StockCountPosition; counted: string; reason: string };
const keyOf = (p: StockCountPosition) => `${p.entryId}|${p.location}`;

/**
 * Kiểm kê theo lô bằng máy quét (kiểm kê xoay vòng một phần kho): chọn kho → quét tem lô → gõ số đếm → lặp lại → lập phiếu
 * kiểm kê. Chỉ các lô đã quét vào phiếu; dòng nào số đếm khác sổ phải ghi lý do. Gửi và ghi sổ phiếu kiểm kê là việc của
 * quản trị viên (máy chủ chặn người khác): quản trị viên thì màn này gửi luôn chờ ghi sổ; người khác lập phiếu nháp để quản
 * trị viên gửi và ghi sổ ở trang Kiểm kê (tách người đếm khỏi người duyệt).
 */
export function CountTask() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  // Gửi phiếu kiểm kê chờ ghi sổ là ô SỬA (chốt 20/09/2026); ghi sổ mới là ô Duyệt, làm ở trang Kiểm kê.
  const canSubmit = useStockMoveAccess()("update");
  const warehouses = useReceiptWarehouses();
  const [warehouseId, setWarehouseId] = useState("");
  const positions = useStockCountPositions(warehouseId, warehouseId.length > 0);
  const [choices, setChoices] = useState<StockCountPosition[]>([]);
  const [current, setCurrent] = useState<StockCountPosition | null>(null);
  const [value, setValue] = useState("");
  const [counted, setCounted] = useState<Map<string, Counted>>(new Map());
  const [reason, setReason] = useState(() => t("scan.count.reasonDefault"));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  useEffect(() => {
    const list = warehouses.data?.data ?? [];
    if (!warehouseId && list.length) setWarehouseId((list.find((w) => w.code === "WH-SG") ?? list[0]!).id);
  }, [warehouses.data, warehouseId]);

  const pick = (p: StockCountPosition) => {
    setCurrent(p);
    setChoices([]);
    setValue(counted.get(keyOf(p))?.counted ?? "");
  };
  const scan = (raw: string) => {
    setError(null);
    setNotice(null);
    const hit = lotScan(raw, parseBarcode);
    if (!hit) return setError(t("scan.parseError", { code: raw }));
    const hits = (positions.data?.data ?? []).filter((p) => p.lotNumber.trim() === hit.lotNumber && sameProduct(hit.productCode, p.productCode));
    if (!hits.length) return setError(t("scan.count.notInWarehouse", { lot: hit.lotNumber }));
    if (hits.length === 1) pick(hits[0]!);
    else { setCurrent(null); setChoices(hits); }
  };
  const record = () => {
    if (!current || !cleanQuantity(value)) return;
    setCounted((prev) => new Map(prev).set(keyOf(current), { position: current, counted: value.trim(), reason: prev.get(keyOf(current))?.reason ?? "" }));
    setCurrent(null);
    setValue("");
  };
  const rows = [...counted.values()];
  const differs = (c: Counted) => Number(c.counted) !== Number(c.position.availableQuantity);
  const needReason = rows.some((c) => differs(c) && c.reason.trim().length < 5);

  const submit = async () => {
    if (busy || !rows.length || needReason) return;
    setBusy(true);
    setError(null);
    try {
      const doc = await apiFetch<{ data: StockCountDto }>("/stock-moves/counts", {
        method: "POST",
        body: {
          requestId: crypto.randomUUID(), kind: "Count", warehouseId, countedDate: today(), reason: reason.trim(), notes: "",
          lines: rows.map((c) => ({ id: crypto.randomUUID(), productId: c.position.productId, entryId: c.position.entryId, location: c.position.location,
            lotNumber: c.position.lotNumber, unit: c.position.unit, counted: c.counted, varianceReason: differs(c) ? c.reason.trim() : "" })),
        },
      });
      if (canSubmit) {
        await apiFetch(`/stock-moves/counts/${doc.data.id}/action`, { method: "POST", body: { revision: doc.data.revision, action: "submit", reason: reason.trim() } });
        setNotice(t("scan.count.done", { no: doc.data.documentNo }));
      } else setNotice(t("scan.count.draftOnly", { no: doc.data.documentNo }));
      setCounted(new Map());
      await qc.invalidateQueries({ queryKey: ["stock-counts"] });
    } catch (e) {
      setError(scanError(e, t, ""));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <FieldLabel htmlFor="scan-count-wh">{t("scan.count.warehouse")}</FieldLabel>
        <Select id="scan-count-wh" containerClassName="w-full" value={warehouseId} disabled={busy || rows.length > 0} onChange={(e) => setWarehouseId(e.target.value)}>
          {warehouses.data?.data.map((w) => <option key={w.id} value={w.id}>{w.name} ({w.code})</option>)}
        </Select>
      </div>
      <ScanField id="scan-count-lot" label={t("scan.scanLot")} placeholder={t("scan.scanLotPlaceholder")} busy={busy || positions.isLoading} autoFocus={!current} onScan={scan} />
      {error && <p role="alert" className="text-sm text-danger">{error}</p>}
      {notice && <p role="status" className="rounded-sm bg-ok-tint px-3 py-2 text-sm text-ok">{notice}</p>}
      {choices.length > 0 && (
        <fieldset className="min-w-0 space-y-2">
          <legend className="text-sm text-ink-soft">{t("scan.count.choose", { lot: choices[0]!.lotNumber })}</legend>
          {choices.map((p) => (
            <Button key={keyOf(p)} variant="secondary" className="h-auto min-h-12 w-full justify-start whitespace-normal py-2 text-left" onClick={() => pick(p)}>
              <span><span className="font-mono">{p.productCode} · {p.location}</span> · {t("scan.count.system")} <span className="font-mono tabular">{formatQty(Number(p.availableQuantity))}</span> <span className="text-2xs text-ink-faint">{p.unit}</span></span>
            </Button>
          ))}
        </fieldset>
      )}
      {current && (
        <div className="space-y-2 rounded-md border border-brand/40 bg-surface p-4">
          <p className="font-mono text-sm font-semibold">{current.productCode} · {current.lotNumber}</p>
          <FieldLabel htmlFor="scan-count-qty">{t("scan.count.enter", { lot: current.lotNumber, location: current.location })}</FieldLabel>
          {/* Khổ điện thoại 390 px không đủ cho ô số + đơn vị + nút trên một hàng: nút xuống hàng và rộng hết bề ngang
              (bấm bằng ngón tay dễ hơn), từ sm trở lên mới nằm cùng hàng như cũ. */}
          <div className="flex flex-wrap items-center gap-2">
            <Input id="scan-count-qty" className="h-12 min-w-0 flex-1 font-mono text-lg" inputMode="decimal" autoFocus value={value}
              onChange={(e) => setValue(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); record(); } }} />
            <span className="text-sm text-ink-soft">{current.unit}</span>
            <Button className="h-12 w-full sm:w-auto" disabled={!cleanQuantity(value)} onClick={record}>{t("scan.count.add")}</Button>
          </div>
        </div>
      )}
      {rows.length === 0 ? <p className="text-sm text-ink-soft">{t("scan.count.empty")}</p> : (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold">{t("scan.count.scanned")} ({rows.length})</h3>
          <ul className="space-y-2">
            {rows.map((c) => (
              <li key={keyOf(c.position)} className="space-y-1 rounded-md border border-line bg-surface p-3 text-sm">
                <p className="font-mono">{c.position.productCode} · {c.position.lotNumber} · {c.position.location}</p>
                <p className="font-mono tabular">
                  {t("scan.count.system")} {formatQty(Number(c.position.availableQuantity))} · {t("scan.count.counted")} {formatQty(Number(c.counted))}
                  {differs(c) && <span className="text-warn"> · {t("scan.count.variance")} {formatQty(Number(c.counted) - Number(c.position.availableQuantity))}</span>}
                  {" "}<span className="text-2xs text-ink-faint">{c.position.unit}</span>
                </p>
                {differs(c) && (
                  <Input className="h-10" aria-label={`${t("scan.count.varianceReason")} ${c.position.lotNumber}`} placeholder={t("scan.count.varianceReason")} value={c.reason}
                    onChange={(e) => { const reasonText = e.target.value; setCounted((prev) => new Map(prev).set(keyOf(c.position), { ...c, reason: reasonText })); }} />
                )}
              </li>
            ))}
          </ul>
          <div>
            <FieldLabel htmlFor="scan-count-reason">{t("scan.count.reason")}</FieldLabel>
            <Input id="scan-count-reason" className="h-12" value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>
          {needReason && <p className="text-sm text-warn">{t("scan.count.varianceRequired")}</p>}
          {!canSubmit && <p className="text-sm text-ink-soft">{t("scan.count.draftHint")}</p>}
          <Button className="h-12 w-full" disabled={busy || needReason || reason.trim().length < 5} onClick={() => void submit()}>{t(canSubmit ? "scan.count.submit" : "scan.count.saveDraft")}</Button>
        </div>
      )}
    </div>
  );
}
